import React, { useContext, useState } from 'react';
import { ReactComponent as Icon } from 'assets/icons/beacon/Information.svg';
import { ReactComponent as Next } from 'assets/icons/beacon/Next.svg';
import { ReactComponent as Back } from 'assets/icons/beacon/Back.svg';
import { ReactComponent as Plus } from 'assets/icons/beacon/Plus.svg';
import { AAMBeaconIconButton, AAMBeaconPopover } from 'components/shared/Beacon';
import BeneficiaryForm from "./ModalSteps/BeneficiaryForm";
import BeneficiaryAllocation from "./ModalSteps/BeneficiaryAllocation";
import BeneficiaryDetails from "./ModalSteps/BeneficiaryDetails";
import BeneficiaryEditForm from "./ModalSteps/BeneficiaryEditForm";
import BeneficiaryManage from "./ModalSteps/BeneficiaryManage";
import { BeneficiaryTOD } from './ModalSteps/BeneficiaryTOD';
import { DeleteBeneConfirmation } from "./ModalSteps/DeleteBeneConfirmation";
import {
  faqAnswers,
  faqQuestion,
  formatStringIfEducationSavings,
  popoverContent,
  renderAllocationsHeaderDetails,
  replaceStringIfEducationSavings,
} from 'services/BeneficiaryV2';
import BeneficiaryContext from './BeneficiaryContext';
import BeneficiariesModalTitle from './BeneficiariesModalTitle';

const ADD_BENEFICIARIES_TEXT = "Add Beneficiaries";
const BENE_HEADER_DISCRIPTION = "Provide the following information about the beneficiary you are naming.";
const ACTION_ZONE_ALIGN_RIGHT = "action-zone-align-right";

export const faqItems = [
  {
    question: faqQuestion.BENEFICIARY_DEFINITION,
    answer: faqAnswers.BENEFICIARY_DEFINITION
  },
  {
    question: faqQuestion.NAME_YOUR_BENEFICIARIES,
    answer: faqAnswers.NAME_YOUR_BENEFICIARIES
  },
  {
    question: faqQuestion.PRIMARY_VS_SECONDARY,
    answer: faqAnswers.PRIMARY_VS_SECONDARY
  },
  {
    question: faqQuestion.ADD_BENEFICIARIES,
    answer: faqAnswers.ADD_BENEFICIARIES
  },
  {
    question: faqQuestion.DECIDE_BENEFICIARIES,
    answer: faqAnswers.DECIDE_BENEFICIARIES
  },
  {
    question: faqQuestion.IRA_BENEFICIARY,
    answer: faqAnswers.IRA_BENEFICIARY
  }
];

export const beneSteps = Object.freeze({
  BENE_TOD_STEP: 1,
  BENE_MANAGE_STEP: 2,
  BENE_EDIT_STEP: 3,
  BENE_ADD_STEP: 4,
  BENE_DETAILS_STEP: 5,
  BENE_ALLOCATION_STEP: 6,
  DELETE_BENE_CONFIRMATION_STEP: 7
});

export const beneManageSteps = Object.freeze({
});

export const DEFAULT_BENEFICIARIES_CONFIG = {
  beneTitle: ADD_BENEFICIARIES_TEXT,
  beneHeaderDiscription: BENE_HEADER_DISCRIPTION,
  beneNextBtnTitle: "Continue",
  beneNextBtnHandler: "handleAddBeneficiaries()",
  className: ACTION_ZONE_ALIGN_RIGHT
};
const getScale = (step) => {
  switch (step) {
    case beneSteps.BENE_DETAILS_STEP:
      return 'maxi';
    case beneSteps.DELETE_BENE_CONFIRMATION_STEP:
      return 'normal';
    default:
      return 'jumbo';
  }
};
export const beneficiariesConfig = (goToPage, beneficiaries, isCustomerWebView, setSelectedBeneficiaryId) => {
  const { selectedGroup, isEsa, isIndividual, isJoint, setEnableErrorMessage } = useContext(BeneficiaryContext);
  const { primary, secondary } = beneficiaries;
  const isManageStepZero = primary.length === 0 && secondary.length === 0;
  const [addLabel, setAddLabel] = useState(formatStringIfEducationSavings("Add Beneficiary", isEsa));
  return {
    [beneSteps.BENE_TOD_STEP]: {
      component: <BeneficiaryTOD isIndividual={isIndividual} isJoint={isJoint}  />,
      scale: getScale(beneSteps.BENE_TOD_STEP),
      next: {
        label: "Continue",
        handler: () => {
          goToPage(beneSteps.BENE_ADD_STEP);
          setSelectedBeneficiaryId(null);
          setEnableErrorMessage(false);
        },
        icon: <Next slot={'prefix'} />
      },
      additionalActionButton: null,
      previous: null,
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.BENE_MANAGE_STEP]: {
      component: <BeneficiaryManage />,
      scale: getScale(beneSteps.BENE_MANAGE_STEP),
      next: {
        label: isManageStepZero ? replaceStringIfEducationSavings("Submit Without Beneficiaries", isEsa) : "Set Allocations",
        disabled: isManageStepZero && isCustomerWebView,
        handler: () => {
          goToPage(beneSteps.BENE_ALLOCATION_STEP);
          setEnableErrorMessage(true);
        },
        icon: <Next slot={'prefix'} />
      },
      additionalActionButton: {
        label: replaceStringIfEducationSavings("Add Another Beneficiary", isEsa),
        handler: () => {
          goToPage(beneSteps.BENE_ADD_STEP);
          setSelectedBeneficiaryId(null);
          setAddLabel(formatStringIfEducationSavings("Add Another Beneficiary", isEsa));
        },
        icon: <Plus slot={'prefix'} />
      },
      previous: null,
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.BENE_EDIT_STEP]: {
      description: <BeneficiariesModalTitle
        title={formatStringIfEducationSavings('Edit Beneficiary', isEsa)}
        description={formatStringIfEducationSavings('Provide the following information about the beneficiary you are naming.', isEsa)}
        selectedGroup={selectedGroup} />,
      component: <BeneficiaryEditForm />,
      scale: getScale(beneSteps.BENE_EDIT_STEP),
      next: {
        label: "Continue",
        handler: () => {
          setEnableErrorMessage(true);
        },
        icon: <Next slot={'prefix'} />
      },
      additionalActionButton: null,
      previous: {
        label: "Back",
        handler: null,
        icon: <Back slot={'prefix'} />
      },
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.BENE_ADD_STEP]: {
      description: <BeneficiariesModalTitle
        title={addLabel}
        description={formatStringIfEducationSavings('Provide the following information about the beneficiary you are naming.', isEsa)}
        selectedGroup={selectedGroup} />,
      component: <BeneficiaryForm />,
      scale: getScale(beneSteps.BENE_ADD_STEP),
      next: {
        label: "Continue",
        handler: () => {
          setEnableErrorMessage(true);
        },
        icon: <Next slot={'prefix'} />
      },
      additionalActionButton: null,
      previous: addLabel === formatStringIfEducationSavings("Add Another Beneficiary", isEsa) ? {
        label: "Back",
        handler: null,
        icon: <Back slot={'prefix'} />
      } : null,
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.BENE_DETAILS_STEP]: {
      component: <BeneficiaryDetails />,
      scale: getScale(beneSteps.BENE_DETAILS_STEP),
      next: {
        label: "Set Allocations",
        handler: () => { },
        icon: <Next slot={'prefix'} />
      },
      additionalActionButton: {
        label: replaceStringIfEducationSavings("Add Another Beneficiary", isEsa),
        handler: () => {
          goToPage(beneSteps.BENE_ADD_STEP);
          setSelectedBeneficiaryId(null);
          setAddLabel(formatStringIfEducationSavings("Add Another Beneficiary", isEsa));
        },
        icon: <Plus slot={'prefix'} />
      },
      previous: {
        label: "Back",
        handler: null,
        icon: <Back slot={'prefix'} />
      },
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.BENE_ALLOCATION_STEP]: {
      description: <BeneficiariesModalTitle
        title={
          <div className={"beacon-on-dark"}>
            {formatStringIfEducationSavings("Set Allocations for Your Beneficiaries", isEsa)}
            <AAMBeaconIconButton id="popover-allocations-trigger" variant='tertiary' aria-label="setAllocations">
              <Icon slot="icon" />
            </AAMBeaconIconButton>
            <AAMBeaconPopover
              opens-by="#popover-allocations-trigger"
              placement={'bottom'}
              >
              <h2 className={'b-font-weight-bold'}>{formatStringIfEducationSavings(popoverContent.BENEFICIARY_ALLOCATIONS.title, isEsa)}</h2>
              <div>{formatStringIfEducationSavings(popoverContent.BENEFICIARY_ALLOCATIONS.answer, isEsa)}</div>
            </AAMBeaconPopover>
          </div>
        }
        description={renderAllocationsHeaderDetails(isEsa)}
        selectedGroup={selectedGroup} />,
      component: <BeneficiaryAllocation />,
      scale: getScale(beneSteps.BENE_ALLOCATION_STEP),
      next: {
        label: replaceStringIfEducationSavings("Confirm Beneficiaries", isEsa),
        handler: () => { },
        icon: null,
        disabled: isCustomerWebView
      },
      additionalActionButton: null,
      previous: {
        label: "Back",
        handler: null,
        icon: <Back slot={'prefix'} />
      },
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    },
    [beneSteps.DELETE_BENE_CONFIRMATION_STEP]: {
      component: <DeleteBeneConfirmation isEsa={isEsa} />,
      scale: getScale(beneSteps.DELETE_BENE_CONFIRMATION_STEP),
      next: {
        label: "Yes, I’m Sure",
        handler: () => { },
        icon: null
      },
      additionalActionButton: null,
      previous: {
        label: "Back",
        handler: null,
        icon: <Back slot={'prefix'} />
      },
      close: {
        label: "Cancel",
        handler: () => { },
        icon: null
      }
    }
  };
};
